/**
 * Máscara pt-BR (milhar/milhão) em campos numéricos permitidos nas respostas Orya.
 */

import {
  COLUMNS_CONFIG_PRODUCAO,
  PRODUCAO_VOLUMETRIA_METRIC_COLUMN_IDS,
} from '../constants/relatoriosColumnsProducaoVolumetria';
import { parseColumnRefKey } from './relatoriosColumnRefs';
import {
  isProducaoVolumetriaMetricColumnId,
  parseProducaoPivotKey,
} from './relatoriosProducaoPivot';

const PT_BR_FORMATTED = /^\d{1,3}(\.\d{3})+(,\d+)?$/;
const CICLO_MENSAL_COLUMN_ID = 'cicloMensal';
const INTEGER_COUNTER_LABELS = new Set([
  'ciclo mensal',
  ...COLUMNS_CONFIG_PRODUCAO.filter((col) =>
    PRODUCAO_VOLUMETRIA_METRIC_COLUMN_IDS.includes(col.id)
  ).map((col) => col.label.toLowerCase()),
]);



const EXCLUDED_FIELD_RE =

  /\bs[eé]rie\b|\bserie\b|\bequipamento\b|\bcliente\b|\bsolicitante\b|\bpatrim|\bmodelo\b|\bresumo\b|\bnome\b|\bdescri[cç][aã]o\b|\btecnico\b|\bt[eé]cnico\b|\bempresa\b|\bcontrato\b|\barea\b|\b[aá]rea\b/i;



const ALLOWED_FIELD_RE =

  /ciclo\s*mensal|\bciclo\b|^os$|\bos\b|produ[cç][aã]o|\bprod\b|medidor\s*atual|med\.?\s*atual|\bdefeito|\bcondi[cç][aã]o\s*final|\btipo\s*(de\s*)?os|\bcontador|volumetria|uptime|\bqtde\b|\bquantidade\b|\bcusto\b|\bchamados\b|\btotal\b/i;



const MONTH_HEADER_RE =

  /^(jan|fev|mar|abr|mai|jun|jul|ago|set|out|nov|dez)\/\d{4}$|^\d{2}\/\d{4}$|^(0?[1-9]|1[0-2])[\s_/-]+\d{4}$/i;



function normalizeFieldLabel(label) {

  return String(label ?? '')

    .replace(/\*\*/g, '')

    .replace(/^[-*•]\s+/, '')

    .trim()

    .toLowerCase();

}



function isMonthColumnHeader(header) {
  return MONTH_HEADER_RE.test(normalizeFieldLabel(header));
}

function resolveIntegerCounterMetricId(header) {
  const raw = String(header ?? '').trim();
  if (!raw) return null;
  if (raw === CICLO_MENSAL_COLUMN_ID) return CICLO_MENSAL_COLUMN_ID;
  if (PRODUCAO_VOLUMETRIA_METRIC_COLUMN_IDS.includes(raw)) return raw;

  const pivot = parseProducaoPivotKey(raw);
  if (pivot?.prodId) return pivot.prodId;

  const ref = parseColumnRefKey(raw);
  if (ref?.id === CICLO_MENSAL_COLUMN_ID) return CICLO_MENSAL_COLUMN_ID;
  if (ref?.id && isProducaoVolumetriaMetricColumnId(ref.id)) return ref.id;

  return null;
}

function isIntegerCounterDisplayField(header) {
  if (resolveIntegerCounterMetricId(header)) return true;

  const h = normalizeFieldLabel(header);
  if (!h || EXCLUDED_FIELD_RE.test(h)) return false;
  if (h === 'ciclo mensal') return true;

  for (const label of INTEGER_COUNTER_LABELS) {
    if (h === label) return true;
    if (h.startsWith(`${label} `) || h.endsWith(` ${label}`)) return true;
    if (h.includes(`${label} (`) || h.includes(`${label} -`) || h.includes(`${label} —`)) {
      return true;
    }
  }

  return false;
}

export function isOryaNumericFormatColumn(header) {
  if (isIntegerCounterDisplayField(header)) return true;

  const h = normalizeFieldLabel(header);
  if (!h) return false;
  if (EXCLUDED_FIELD_RE.test(h)) return false;
  if (isMonthColumnHeader(h)) return true;

  return ALLOWED_FIELD_RE.test(h);
}



function isOryaNumericFormatLabel(label) {

  const raw = String(label ?? '')

    .replace(/\*\*/g, '')

    .replace(/^[-*•]\s+/, '')

    .trim();

  if (!raw) return false;



  const l = raw.toLowerCase();

  if (EXCLUDED_FIELD_RE.test(l)) return false;

  if (/^s[eé]rie\s*:?$/i.test(l) || /^s[eé]rie\b/i.test(l)) return false;

  if (isIntegerCounterDisplayField(raw)) return true;

  if (ALLOWED_FIELD_RE.test(l)) return true;

  const dashParts = raw.split(/\s[-–—]\s/);

  if (dashParts.length >= 2) {

    const metric = dashParts.slice(0, -1).join(' - ');

    const monthPart = dashParts[dashParts.length - 1];

    if (ALLOWED_FIELD_RE.test(metric.toLowerCase())) return true;

    if (isMonthColumnHeader(monthPart)) return ALLOWED_FIELD_RE.test(metric.toLowerCase());

  }



  return false;

}



function parseLooseNumber(raw) {

  const s = String(raw).trim();

  if (!s || !/^-?[\d.,]+$/.test(s)) return null;

  if (PT_BR_FORMATTED.test(s)) {

    const n = Number(s.replace(/\./g, '').replace(',', '.'));

    return Number.isFinite(n) ? n : null;

  }



  let norm = s;

  const hasComma = norm.includes(',');

  const hasDot = norm.includes('.');



  if (hasComma && hasDot) {

    norm = norm.replace(/\./g, '').replace(',', '.');

  } else if (hasComma) {

    norm = norm.replace(',', '.');

  } else if (hasDot) {

    const [, frac] = norm.split('.');

    if (!frac || frac.length > 2) norm = norm.replace(/\./g, '');

  }



  const n = Number(norm);

  return Number.isFinite(n) ? n : null;

}



export function formatOryaDisplayNumber(value) {

  const n = typeof value === 'number' ? value : parseLooseNumber(value);

  if (n == null) return String(value ?? '');

  const isInt = Math.abs(n - Math.round(n)) < 1e-9;

  if (isInt) return Math.round(n).toLocaleString('pt-BR', { maximumFractionDigits: 0 });

  return n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

}

export function isOryaUptimeLabel(label) {
  return /\buatime\b/i.test(normalizeFieldLabel(label));
}

export function resolveOryaChartSeriesFormat(item) {
  if (!item) return undefined;
  if (item.format === 'percent') return 'percent';
  const label = item.label || item.name || item.key || '';
  return isOryaUptimeLabel(label) ? 'percent' : undefined;
}

/** Uptime em gráficos: decimal (0,89) ou percentual bruto (89) → "89%". */
export function formatOryaUptimePercent(value) {
  const n = typeof value === 'number' ? value : parseLooseNumber(value);
  if (n == null) return String(value ?? '');

  let pct = n;
  if (pct > 0 && pct <= 1) pct *= 100;
  pct = Math.min(100, Math.max(0, pct));

  const isInt = Math.abs(pct - Math.round(pct)) < 1e-9;
  return `${pct.toLocaleString('pt-BR', {
    minimumFractionDigits: isInt ? 0 : 2,
    maximumFractionDigits: 2,
  })}%`;
}

export function formatOryaChartSeriesValue(value, format) {
  if (format === 'percent') return formatOryaUptimePercent(value);
  return formatOryaDisplayNumber(value);
}

export function normalizeOryaChartSeries(series, spec = {}) {
  if (series?.length) {
    return series.map((s) => ({
      ...s,
      label: s.label || s.name || s.key,
      format: resolveOryaChartSeriesFormat(s),
    }));
  }
  const key = spec.valueKey || 'value';
  const label = spec.yLabel || key;
  return [{ key, label, format: resolveOryaChartSeriesFormat({ label, key }) }];
}



function formatOryaNumericCellValue(raw) {

  const trimmed = String(raw ?? '').trim();

  if (!trimmed || trimmed === '—') return trimmed;

  if (/n[aã]o\s+possui\s+resultado/i.test(trimmed)) return trimmed;

  if (/[a-zA-Z]/.test(trimmed)) return trimmed;



  const n = parseLooseNumber(trimmed);

  if (n == null) return trimmed;

  if (PT_BR_FORMATTED.test(trimmed)) return trimmed;

  return formatOryaDisplayNumber(n);

}



export function formatOryaTableCellForDisplay(value, columnHeader) {

  const raw = String(value ?? '');

  if (!raw || !isOryaNumericFormatColumn(columnHeader)) return raw;

  return formatOryaNumericCellValue(raw);

}



export function formatOryaProseLineForDisplay(line) {

  const text = String(line ?? '');

  if (!text) return text;



  return text

    .split(/(\s*·\s*)/)

    .map((part) => {

      if (/^\s*·\s*$/.test(part)) return part;



      const colonIdx = part.lastIndexOf(':');

      if (colonIdx <= 0) return part;



      const label = part.slice(0, colonIdx);

      const value = part.slice(colonIdx + 1);

      if (!isOryaNumericFormatLabel(label)) return part;



      const trimmedValue = value.trim();

      const formatted = formatOryaNumericCellValue(trimmedValue);

      if (formatted === trimmedValue) return part;



      const lead = value.match(/^\s*/)?.[0] || '';

      return `${part.slice(0, colonIdx + 1)}${lead}${formatted}`;

    })

    .join('');

}



export function formatOryaMarkdownTextForDisplay(text) {

  return formatOryaProseLineForDisplay(String(text ?? ''));

}


